import { MUSIC } from './audio-catalogue';
import type { Vec } from '../destruction/structure';
export type Sound =
  | 'brick'
  | 'concrete'
  | 'wood'
  | 'metal'
  | 'glass'
  | 'crack'
  | 'rubble'
  | 'blast'
  | 'build'
  | 'door'
  | 'footstep'
  | 'brake'
  | 'warning';
export interface Soundscape {
  id: string;
  sample: 'fire' | 'engine' | 'siren' | 'aircraft' | 'helicopter' | 'morning' | 'waves';
  gain: number;
  point: Vec;
  rate?: number;
}
interface Deck {
  element: HTMLAudioElement;
  source: MediaElementAudioSourceNode;
  gain: GainNode;
  track: number;
}
interface Loop {
  source: AudioBufferSourceNode;
  gain: GainNode;
  pan: StereoPannerNode;
  retiring?: boolean;
}
const prefixes: Partial<Record<Sound, string>> = {
  brick: 'impactMining',
  concrete: 'impactMining',
  wood: 'impactWood_heavy',
  metal: 'impactMetal_heavy',
  glass: 'impactGlass_medium',
  crack: 'impactPlate_heavy',
  rubble: 'impactMining',
  build: 'impactGeneric_light',
  footstep: 'footstep_concrete',
  brake: 'impactMetal_heavy',
};
/** Local samples, two streamed music decks, bounded effects and a measured limiter. */
export class AudioSystem {
  supported = typeof globalThis.AudioContext === 'function';
  onUnavailable: () => void = () => {};
  context?: AudioContext;
  master?: GainNode;
  volume = 0.45;
  effectsVolume = 0.9;
  musicVolume = 0.35;
  ambienceVolume = 0.65;
  sirenVolume = 0.2;
  sirenMuted = false;
  paused = false;
  voices = 0;
  focus: Vec = { x: 0, y: 0, z: 0 };
  private effects?: GainNode;
  private music?: GainNode;
  private ambience?: GainNode;
  private sirens?: GainNode;
  private limiter?: AudioWorkletNode;
  private initialising?: Promise<void>;
  private decks: Deck[] = [];
  private current = 0;
  private fadeAt?: number;
  private fadeLoading = false;
  private bag: number[] = [];
  private cycles = 0;
  private started = false;
  private active = new Set<AudioBufferSourceNode>();
  private loops = new Map<string, Loop>();
  private loadingLoops = new Set<string>();
  private buffers = new Map<string, Promise<AudioBuffer>>();
  private last = new Map<string, number>();
  private variants = new Map<string, number>();
  private epoch = 0;
  private sceneAge = 0;
  private duckUntil = 0;
  private failures: string[] = [];
  private failedUntil = new Map<string, number>();
  private failed(message: string): void {
    if (this.failures.at(-1) !== message) this.failures.push(message);
    if (this.failures.length > 32) this.failures.shift();
  }
  private outputMeter: Record<string, number> = {};
  private captureCallback?: (blob: Blob) => void;
  private fadeCount = 0;
  private played: number[] = [];
  start(): void {
    if (!this.supported) return;
    this.initialising ??= this.initialize().catch((e) => {
      this.failed(String(e));
      this.supported = false;
      this.onUnavailable();
    });
    void this.initialising.then(() => {
      if (!this.paused) {
        void this.context?.resume().catch(() => {});
        this.resumeMusic();
      }
    });
  }
  private async initialize(): Promise<void> {
    const ctx = new AudioContext({ latencyHint: 'interactive' });
    this.context = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = 0;
    this.effects = ctx.createGain();
    this.music = ctx.createGain();
    this.ambience = ctx.createGain();
    this.sirens = ctx.createGain();
    for (const g of [this.effects, this.music, this.ambience, this.sirens]) g.connect(this.master);
    await ctx.audioWorklet.addModule('/audio/limiter.mjs');
    this.limiter = new AudioWorkletNode(ctx, 'city-output-limiter', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });
    this.limiter.port.onmessage = (e) => {
      if (e.data.meter) this.outputMeter = e.data.meter;
      if (e.data.capture) {
        const data = new Float32Array(e.data.capture),
          bytes = new ArrayBuffer(44 + data.length * 2),
          v = new DataView(bytes);
        const str = (o: number, s: string) => {
          for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i));
        };
        str(0, 'RIFF');
        v.setUint32(4, bytes.byteLength - 8, true);
        str(8, 'WAVE');
        str(12, 'fmt ');
        v.setUint32(16, 16, true);
        v.setUint16(20, 1, true);
        v.setUint16(22, 2, true);
        v.setUint32(24, e.data.rate, true);
        v.setUint32(28, e.data.rate * 4, true);
        v.setUint16(32, 4, true);
        v.setUint16(34, 16, true);
        str(36, 'data');
        v.setUint32(40, data.length * 2, true);
        for (let i = 0; i < data.length; i++) v.setInt16(44 + i * 2, Math.round(data[i] * 32767), true);
        this.captureCallback?.(new Blob([bytes], { type: 'audio/wav' }));
        this.captureCallback = undefined;
      }
    };
    this.master.connect(this.limiter);
    this.limiter.connect(ctx.destination);
    for (let i = 0; i < 2; i++) {
      const element = new Audio();
      element.preload = 'auto';
      element.loop = false;
      const source = ctx.createMediaElementSource(element),
        gain = ctx.createGain();
      gain.gain.value = 0;
      source.connect(gain);
      gain.connect(this.music);
      element.onerror = () => {
        this.failed(`Music unavailable: ${element.currentSrc}`);
      };
      this.decks.push({ element, source, gain, track: 0 });
    }
    this.loadDeck(0, 0);
    this.played.push(0);
    this.loadDeck(1, this.nextIndex(0));
    this.started = true;
    this.apply();
    if (!this.paused) this.resumeMusic();
  }
  private nextIndex(last: number): number {
    if (!this.bag.length) {
      this.bag = MUSIC.map((_, i) => i).filter((i) => this.cycles > 0 || i !== last);
      this.cycles++;
      for (let i = this.bag.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [this.bag[i], this.bag[j]] = [this.bag[j], this.bag[i]];
      }
      if (this.bag[this.bag.length - 1] === last)
        [this.bag[0], this.bag[this.bag.length - 1]] = [this.bag[this.bag.length - 1], this.bag[0]];
    }
    return this.bag.pop()!;
  }
  private loadDeck(deck: number, track: number): void {
    const d = this.decks[deck];
    d.track = track;
    d.element.src = MUSIC[track].url;
    d.element.load();
  }
  private resumeMusic(): void {
    if (!this.started || this.paused || this.volume === 0 || this.musicVolume === 0) return;
    const d = this.decks[this.current];
    if (this.fadeAt === undefined)
      d.gain.gain.setTargetAtTime(MUSIC[d.track].gain, this.context!.currentTime, 0.3);
    void d.element.play().catch(() => {});
    if (this.fadeAt !== undefined) void this.decks[1 - this.current].element.play().catch(() => {});
  }
  async skip(): Promise<void> {
    this.paused = false;
    this.start();
    await this.initialising;
    await this.context?.resume();
    this.resumeMusic();
    if (this.decks.length && this.fadeAt === undefined) void this.beginFade();
  }
  private async beginFade(): Promise<void> {
    if (this.fadeAt !== undefined || this.fadeLoading || this.paused) return;
    const next = this.decks[1 - this.current];
    this.fadeLoading = true;
    try {
      await next.element.play();
      this.fadeAt = this.context!.currentTime;
      this.fadeCount++;
      this.played.push(next.track);
      if (this.played.length > 32) this.played.shift();
      if (this.paused) next.element.pause();
    } catch (e) {
      this.failed(String(e));
    } finally {
      this.fadeLoading = false;
    }
  }
  update(dt: number, focus: Vec, scene: Soundscape[]): void {
    this.focus = { ...focus };
    const ctx = this.context;
    if (!ctx || !this.started || this.paused) return;
    const current = this.decks[this.current];
    if (
      this.musicVolume > 0 &&
      this.volume > 0 &&
      this.fadeAt === undefined &&
      current.element.duration - current.element.currentTime < 6
    )
      void this.beginFade();
    if (this.fadeAt !== undefined) {
      const t = Math.min(1, (ctx.currentTime - this.fadeAt) / 6),
        next = this.decks[1 - this.current];
      current.gain.gain.value = Math.cos((t * Math.PI) / 2) * MUSIC[current.track].gain;
      next.gain.gain.value = Math.sin((t * Math.PI) / 2) * MUSIC[next.track].gain;
      if (t === 1) {
        current.element.pause();
        current.gain.gain.value = 0;
        this.current = 1 - this.current;
        this.fadeAt = undefined;
        this.loadDeck(1 - this.current, this.nextIndex(next.track));
      }
    }
    this.music!.gain.setTargetAtTime(
      this.musicVolume * (ctx.currentTime < this.duckUntil ? 0.17 : 0.24),
      ctx.currentTime,
      0.6,
    );
    this.sceneAge += dt;
    if (this.sceneAge < 0.2) return;
    this.sceneAge = 0;
    const wanted = scene
      .map((s) => ({ ...s, gain: s.gain * this.attenuation(s.point) }))
      .filter((s) => s.gain > 0.005)
      .sort((a, b) => b.gain - a.gain)
      .slice(0, 6);
    const ids = new Set(wanted.map((s) => s.id));
    for (const [id, loop] of this.loops)
      if (!ids.has(id) && !loop.retiring) {
        loop.retiring = true;
        loop.gain.gain.setTargetAtTime(0, ctx.currentTime, 0.2);
        loop.source.stop(ctx.currentTime + 0.6);
      }
    for (const item of wanted) {
      const loop = this.loops.get(item.id);
      if (loop) {
        if (loop.retiring) continue;
        loop.gain.gain.setTargetAtTime(item.gain, ctx.currentTime, 0.4);
        loop.source.playbackRate.setTargetAtTime(item.rate ?? 1, ctx.currentTime, 0.3);
        loop.pan.pan.setTargetAtTime(this.pan(item.point), ctx.currentTime, 0.2);
        continue;
      }
      if (this.loadingLoops.has(item.id) || this.loops.size + this.loadingLoops.size >= 6) continue;
      this.loadingLoops.add(item.id);
      const epoch = this.epoch;
      void this.buffer(`/audio/effects/${item.sample}.wav`)
        .then((buffer) => {
          if (epoch !== this.epoch || this.paused || this.loops.has(item.id)) return;
          const source = ctx.createBufferSource(),
            gain = ctx.createGain(),
            pan = ctx.createStereoPanner();
          source.buffer = buffer;
          source.loop = true;
          source.playbackRate.value = item.rate ?? 1;
          gain.gain.value = 0;
          pan.pan.value = this.pan(item.point);
          source.connect(pan);
          pan.connect(gain);
          gain.connect(item.sample === 'siren' ? this.sirens! : this.ambience!);
          source.start();
          gain.gain.setTargetAtTime(item.gain, ctx.currentTime, 0.5);
          this.loops.set(item.id, { source, gain, pan });
          source.onended = () => {
            source.disconnect();
            pan.disconnect();
            gain.disconnect();
            if (this.loops.get(item.id)?.source === source) this.loops.delete(item.id);
          };
        })
        .catch(() => {})
        .finally(() => {
          if (epoch === this.epoch) this.loadingLoops.delete(item.id);
        });
    }
  }
  private attenuation(p?: Vec): number {
    if (!p) return 1;
    const distance = Math.hypot(p.x - this.focus.x, p.z - this.focus.z, (p.y - this.focus.y) * 0.35);
    return 1 / (1 + (distance / 55) ** 2);
  }
  private pan(p?: Vec): number {
    return p ? Math.max(-0.85, Math.min(0.85, (p.x - this.focus.x) / 85)) : 0;
  }
  private buffer(url: string): Promise<AudioBuffer> {
    if ((this.failedUntil.get(url) ?? 0) > performance.now())
      return Promise.reject(new Error(`Audio temporarily unavailable: ${url}`));
    let pending = this.buffers.get(url);
    if (!pending) {
      if (this.buffers.size >= 32) this.buffers.delete(this.buffers.keys().next().value!);
      pending = fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(`Audio ${r.status}: ${url}`);
          return r.arrayBuffer();
        })
        .then((b) => this.context!.decodeAudioData(b))
        .catch((e) => {
          this.failed(String(e));
          this.failedUntil.set(url, performance.now() + 30000);
          if (this.failedUntil.size > 32) this.failedUntil.delete(this.failedUntil.keys().next().value!);
          this.buffers.delete(url);
          throw e;
        });
      this.buffers.set(url, pending);
    }
    return pending;
  }
  play(type: Sound, power = 1, point?: Vec): void {
    const ctx = this.context;
    if (!ctx || !this.started || this.paused || this.volume === 0 || this.voices >= 14) return;
    const now = ctx.currentTime;
    if (now - (this.last.get(type) ?? -100) < (type === 'rubble' ? 0.15 : 0.07)) return;
    const attenuation = this.attenuation(point);
    if (attenuation * power < 0.025) return;
    this.last.set(type, now);
    const variant = ((this.variants.get(type) ?? -1) + 1) % 3;
    this.variants.set(type, variant);
    const url =
      type === 'blast'
        ? '/audio/effects/explosion.wav'
        : type === 'warning'
          ? '/audio/effects/siren.wav'
          : type === 'door'
            ? '/audio/effects/bus-door.wav'
            : `/audio/effects/${prefixes[type]}_00${variant}.ogg`;
    const epoch = this.epoch;
    this.voices++;
    void this.buffer(url)
      .then((buffer) => {
        if (epoch !== this.epoch || this.paused) {
          this.voices = Math.max(0, this.voices - 1);
          return;
        }
        const source = ctx.createBufferSource(),
          gain = ctx.createGain(),
          pan = ctx.createStereoPanner();
        source.buffer = buffer;
        source.playbackRate.value = type === 'blast' ? 1 : 0.92 + Math.random() * 0.16;
        // Former 0.055 amplitude compensated by 5x before group/master gains
        // and limiting. Already-mastered music never receives this multiplier.
        gain.gain.value = Math.min(0.75, power * 0.275) * attenuation;
        pan.pan.value = this.pan(point);
        source.connect(pan);
        pan.connect(gain);
        gain.connect(type === 'warning' ? this.sirens! : this.effects!);
        this.active.add(source);
        source.onended = () => {
          source.disconnect();
          pan.disconnect();
          gain.disconnect();
          this.active.delete(source);
          this.voices = Math.max(0, this.voices - 1);
        };
        source.start();
        if (type === 'warning') source.stop(ctx.currentTime + 1.4);
        if (type === 'blast' || type === 'crack') this.duckUntil = ctx.currentTime + 2.5;
      })
      .catch(() => {
        this.voices = Math.max(0, this.voices - 1);
      });
  }
  clear(): void {
    this.epoch++;
    for (const source of this.active) {
      try {
        source.stop();
      } catch {
        /* already ended */
      }
    }
    for (const loop of this.loops.values()) loop.source.stop();
    this.loops.clear();
    this.loadingLoops.clear();
    this.last.clear();
  }
  get activeVoices(): number {
    return this.voices + this.loops.size + this.decks.filter((d) => !d.element.paused).length;
  }
  vehicle(_speed: number, _active: boolean): void {
    /* Spatial sampled traffic loops are controlled by update(). */
  }
  apply(): void {
    const ctx = this.context;
    if (!ctx || !this.master) return;
    this.master.gain.setTargetAtTime(this.volume, ctx.currentTime, 0.2);
    this.effects!.gain.setTargetAtTime(this.effectsVolume, ctx.currentTime, 0.1);
    this.ambience!.gain.setTargetAtTime(this.ambienceVolume, ctx.currentTime, 0.2);
    // 0.65 is the previous default ambience gain. The new 0.2 siren default
    // applies one 80% amplitude reduction without also reducing other sounds.
    this.sirens!.gain.setTargetAtTime(this.sirenMuted ? 0 : this.sirenVolume * 0.65, ctx.currentTime, 0.1);
    this.music!.gain.setTargetAtTime(this.musicVolume * 0.24, ctx.currentTime, 0.3);
    if (this.volume === 0 || this.musicVolume === 0) for (const d of this.decks) d.element.pause();
    else this.resumeMusic();
  }
  pause(paused: boolean): void {
    this.paused = paused;
    if (paused) {
      for (const d of this.decks) d.element.pause();
      void this.context?.suspend().catch(() => {});
    } else {
      void this.context?.resume().catch(() => {});
      this.resumeMusic();
    }
  }
  async audition(type: Sound): Promise<void> {
    this.paused = false;
    this.start();
    await this.initialising;
    await this.context?.resume();
    this.resumeMusic();
    this.play(type, 1.5);
  }
  async record(seconds: number, callback: (blob: Blob) => void): Promise<void> {
    this.paused = false;
    this.start();
    await this.initialising;
    if (!this.limiter) return;
    await this.context?.resume();
    this.resumeMusic();
    this.captureCallback = callback;
    this.limiter.port.postMessage({ record: seconds });
  }
  inspect(): Record<string, unknown> {
    return {
      supported: this.supported,
      context: this.context?.state ?? 'not started',
      voices: this.activeVoices,
      effectVoices: this.voices,
      loops: this.loops.size,
      cachedSamples: this.buffers.size,
      musicStreams: this.decks.length,
      currentTrack: this.decks[this.current] ? MUSIC[this.decks[this.current].track].title : null,
      trackSeconds: this.decks[this.current]?.element.currentTime ?? 0,
      playlistMinutes: MUSIC.reduce((s, t) => s + t.seconds / 60, 0),
      crossfades: this.fadeCount,
      played: this.played.map((i) => MUSIC[i].title),
      limiter: !!this.limiter,
      master: this.volume,
      effects: this.effectsVolume,
      music: this.musicVolume,
      ambience: this.ambienceVolume,
      sirens: this.sirenVolume,
      sirenMuted: this.sirenMuted,
      sirenBusGain: this.sirens?.gain.value ?? null,
      failures: [...this.failures],
      meter: { ...this.outputMeter },
    };
  }
}
